/**
 * Example cache in ResourceCache:
 * _cache = [
 *   {
 *     key: "fetchAllBooks",
 *     data: ["book1", "book2", "book3"],
 *     ttl: 1593917959867
 *   },
 *   {
 *     key: "fetchAllBooks",
 *     data: ["book1", "book2", "book3"],
 *     ttl: 1593917900000
 *   },
 * ]
 */
import { fromEnv } from "./fromEnv";

interface CacheObject {
  key: string;
  expiry: number;
  data: any;
}

export abstract class AsyncCache {
  protected _cache: CacheObject[] = [];

  addToCache(cacheObject: CacheObject) {
    // Remove all cache objects with same key before storing new cache
    this.deleteFromCache(cacheObject.key);
    this._cache.push(cacheObject);
  }

  /**
   * Returns a cache object where the TTL is greater than current time in millisecond
   * @param key
   */
  getFromCache(key: string): CacheObject | undefined {
    return this._cache.find((c) => c.key === key && c.expiry > Date.now());
  }

  /**
   * If no key is provided, will delete all cache entries of the current resource. Used by the flush() strategy.
   * @param key
   */
  deleteFromCache(key?: string) {
    if (key) {
      this._cache = this._cache.filter((c) => c.key !== key);
      return;
    }
    this._cache = [];
  }
}

/**
 * Caches content based on parameters of the wrapped function.
 *
 * Example:
 *
 * \@CachingStrategies.basic()
 * getAllBooks (page = 1) {
 *   return await APIService.get(ENV.API + "/books")
 * }
 *
 * in the `getAllBooks` function, the cache will be based on the value of the `page` argument.
 */
const apply = <T extends AsyncCache>(ttl = 1000 * 60 * 5) => {
  return (_target: T, _key: string, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;
    // Must be written in function declaration format to correctly capture the context.
    descriptor.value = async function (...args: any[]) {
      const asyncService = this as T;

      // Converts the func params into base64 so it can be used as the cache key
      const _plainKey = originalMethod.name + JSON.stringify(args);
      const _cacheKey = btoa(_plainKey);

      const fromCache = asyncService.getFromCache(_cacheKey);
      if (fromCache) {
        /* istanbul ignore next */
        if (!fromEnv<boolean>("REACT_APP_DISABLE_ASYNC_DELAY")) {
          // Add 300ms of artificial delay so UI can show loading state
          await new Promise((res) => setTimeout(res, 300));
        }
        return fromCache.data;
      }

      const result = await originalMethod.apply(asyncService, args);
      asyncService.addToCache({
        key: _cacheKey,
        data: result,
        expiry: Date.now() + ttl,
      });
      return result;
    };
  };
};

/**
 * Flushes the cache of the current AsyncService after the function has completed execution.
 */
const flush = <T extends AsyncCache>() => {
  return (_target: T, _key: string, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;
    descriptor.value = async function (...args: any[]) {
      const asyncService = this as T;
      const response = await originalMethod.apply(asyncService, args);
      asyncService.deleteFromCache();
      return response;
    };
  };
};

export const CachingStrategies = {
  apply,
  flush,
};
